import { LAST_PRICE_DASH_PX, LAST_PRICE_LINE_WIDTH_PX } from "../constants";
import { axisFont } from "../text/axisFont";
import { formatPrice } from "../axes/priceFormat";
import { clampBadgeY, drawPriceBadge, priceBadgeRect, priceBadgeTextX } from "./priceBadge";
import { snapLine } from "./grid";
import { POSITION_DIRECTION, POSITION_PIN_ARROW } from "./positions/positionConstants";
import { drawPinArrow } from "./positions/positionPills";

// A pinned badge grows to the left by its ▲ / ▼ and the gap after it, so the price stays where it was
const PIN_ARROW_SPACE = POSITION_PIN_ARROW.width + POSITION_PIN_ARROW.gap;

// Outside the pane the price is pinned to the edge it is beyond, like position lines
const pinOf = (y, pane) => {
  if (y < 0) return POSITION_DIRECTION.UP;
  if (y > pane.height) return POSITION_DIRECTION.DOWN;

  return null;
};

/**
 * Last price of the frame (po-chart-visual-spec.md §12.4): the series'
 * last value placed on the price scale. It is never lost: outside the pane
 * (history with other prices in view) it is pinned to the top or bottom edge.
 *
 * @param {{ index: number, price: number, color: object } | null} lastValue - mainSeries.lastValue
 * @param {object} priceScale
 * @param {{ height: number }} pane
 * @returns {{ index: number, price: number, color: object, y: number, pin: string|null } | null}
 *   null without bars; pin: POSITION_DIRECTION where the price is when it is outside the pane
 */
export const resolveLastPrice = (lastValue, priceScale, pane) => {
  if (!lastValue) return null;

  const y = priceScale.priceToY(lastValue.price);

  return { ...lastValue, y, pin: pinOf(y, pane) };
};

/**
 * Thin dashed line across the pane at the last price in the colour of its
 * badge, like moon.com; on the text layer under the badges, so position
 * cards and pills cover it. None while the price is pinned outside the pane.
 *
 * @param {object} textLayer
 * @param {{ y: number, pin: string|null, color: { css: string } }} lastPrice - from resolveLastPrice
 * @param {{ width: number }} pane - CSS px
 */
export const drawLastPriceLine = (textLayer, lastPrice, pane) => {
  if (lastPrice.pin) return;

  const { context, pixelRatio } = textLayer;
  const lineWidth = Math.max(1, Math.floor(LAST_PRICE_LINE_WIDTH_PX * pixelRatio));
  const y = snapLine(lastPrice.y, pixelRatio, lineWidth) / pixelRatio;

  context.save();
  context.lineWidth = lineWidth / pixelRatio;
  context.strokeStyle = lastPrice.color.css;
  context.setLineDash(LAST_PRICE_DASH_PX);
  context.beginPath();
  context.moveTo(0, y);
  context.lineTo(pane.width, y);
  context.stroke();
  context.restore();
};

/**
 * Where the last price badge stands on the price axis; it covers position
 * badges at the same height instead of moving them (moon.com). A pinned
 * price keeps its badge whole inside the pane at the edge, wider on the
 * left by the ▲ / ▼ drawn inside it.
 *
 * @param {{ y: number, pin: string|null }} lastPrice - from resolveLastPrice
 * @param {{ width: number, height: number }} pane
 * @param {number} priceAxisWidth
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export const lastPriceBadgeRect = (lastPrice, pane, priceAxisWidth) => {
  const rect = priceBadgeRect(clampBadgeY(lastPrice.y, pane), pane, priceAxisWidth);

  if (!lastPrice.pin) return rect;

  return { ...rect, x: rect.x - PIN_ARROW_SPACE, width: rect.width + PIN_ARROW_SPACE };
};

/**
 * Badge on the price axis: the colour of the line, a light price at the
 * full pair precision (E9), shaped like all price badges (priceBadge.js).
 * The crosshair price label covers it while at the same height. Pinned to
 * an edge, ▲ / ▼ (as on a pinned position pill) stands inside it before the
 * price, in the colour of the text: outside the badge the «›» button and
 * the high / low labels would cover it.
 *
 * @param {object} textLayer
 * @param {{ price: number, pin: string|null, color: { css: string } }} lastPrice - from resolveLastPrice,
 *   the real price
 * @param {object} params
 * @param {{ x: number, y: number, width: number, height: number }} params.rect - from lastPriceBadgeRect
 * @param {number} params.decimals - price precision of the pair
 * @param {{ colors: object, fontFamily: string }} params.theme
 */
export const drawLastPriceBadge = (textLayer, lastPrice, { rect, decimals, theme }) => {
  const color = theme.colors.lastPriceText.css;
  const textIndent = lastPrice.pin ? PIN_ARROW_SPACE : 0;

  drawPriceBadge(textLayer, rect, formatPrice(lastPrice.price, decimals), {
    font: axisFont(theme.fontFamily),
    background: lastPrice.color.css,
    color,
    textIndent,
  });

  if (lastPrice.pin) {
    drawPinArrow(textLayer.context, {
      x: priceBadgeTextX(rect),
      centreY: rect.y + rect.height / 2,
      pin: lastPrice.pin,
      color,
    });
  }
};
